/**
 * Number and rate formatting for the Analytics tool — one place, so every figure
 * reads the same way. A rate always carries its denominator (docs/ANALYTICS-TOOL-DESIGN.md
 * principle 5): "12% · 18 of 150", never a bare percentage.
 */
export const fmt = (n) => (n == null || Number.isNaN(n) ? '—' : Number(n).toLocaleString('en-GB'));

export function compact(n) {
  if (n == null) return '—';
  const a = Math.abs(n);
  if (a >= 1e6) return `${(n / 1e6).toFixed(1)}m`;
  if (a >= 1e4) return `${Math.round(n / 1e3)}k`;
  if (a >= 1e3) return `${(n / 1e3).toFixed(1)}k`;
  return String(n);
}

/** 0–100 with sensible precision: 0.4% stays 0.4%, 12.3% becomes 12%. */
export function pct(n, d) {
  if (!d) return '—';
  const v = (n / d) * 100;
  if (v === 0) return '0%';
  if (v < 1) return `${v.toFixed(1)}%`;
  return `${Math.round(v)}%`;
}

/** "12% · 18 of 150" */
export const rateWith = (n, d) => (d ? `${pct(n, d)} · ${fmt(n)} of ${fmt(d)}` : '—');

/** Relative change, or null when there is nothing to compare with. */
export function change(cur, prev) {
  if (cur == null || prev == null || prev === 0) return null;
  return (cur - prev) / prev;
}

/** A sample too small to trust — shown greyed, never hidden. */
export const SMALL_SAMPLE = 20;

export function dayLabel(day) {
  const d = new Date(`${day}T12:00:00Z`);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
}

export function durationLabel(sec) {
  if (sec == null) return '—';
  if (sec < 60) return `${Math.round(sec)}s`;
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return s ? `${m}m ${s}s` : `${m}m`;
}

/** "3 min ago", "5 hours ago", "12 days ago", or the date itself past a couple of months. */
export function agoLabel(msValue, now = Date.now()) {
  if (msValue == null) return 'never';
  const s = Math.max(0, (now - msValue) / 1000);
  if (s < 90) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400 * 1.5) return `${Math.round(s / 3600)} hours ago`.replace(/^1 hours/, '1 hour');
  if (s < 86400 * 60) return `${Math.round(s / 86400)} days ago`;
  return new Date(msValue).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** "2 Jun 2026" from "2026-06-02". */
export const dateLabel = (day) => new Date(`${day}T12:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });

/** "1 like", "3 likes" — the count formatted, the noun agreeing with it. */
export const plural = (n, one, many = `${one}s`) => `${fmt(n)} ${n === 1 ? one : many}`;
